import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot, setDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import CreateEventModal from './CreateEventModal';
import './LandingPage.css';

export default function LandingPage({ user, userProfile, onSignOut }) {
    const [partyCode, setPartyCode] = useState('');
    const [myParties, setMyParties] = useState([]);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const navigate = useNavigate();



    // Listen for My Parties
    useEffect(() => {
        if (!user) {
            setMyParties([]);
            return;
        }

        const q = query(
            collection(db, 'parties'),
            where('participants', 'array-contains', user.uid)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const parties = snapshot.docs.map(doc => ({
                code: doc.id,
                ...doc.data()
            }));
            // Sort by date or creation? Let's sort by date descending (newest first)
            parties.sort((a, b) => {
                const dateA = a.date ? new Date(a.date).getTime() : 0;
                const dateB = b.date ? new Date(b.date).getTime() : 0;
                // If dates match (or missing), sort by created
                if (dateA === dateB) {
                    const createdA = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
                    const createdB = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
                    return createdB - createdA;
                }
                return dateB - dateA;
            });
            setMyParties(parties);
        });

        return () => unsubscribe();
    }, [user]);

    const formatCode = (val) => {
        const cleaned = val.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
        const truncated = cleaned.slice(0, 6);
        if (truncated.length > 3) {
            return `${truncated.slice(0, 3)}-${truncated.slice(3)}`;
        }
        return truncated;
    };

    const handleInput = (e) => {
        const val = e.target.value;
        setPartyCode(formatCode(val));
    };

    const handleJoin = (e, codeOverride) => {
        if (e) e.preventDefault();
        const codeToJoin = codeOverride || partyCode;
        const rawCode = codeToJoin.replace(/-/g, '');

        if (rawCode.length === 6) {
            navigate(`/party/${rawCode}`);
        }
    };

    const handleCreate = async (details) => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let result = '';
        for (let i = 0; i < 6; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }

        // Persistent Creation
        if (user) {
            try {
                await setDoc(doc(db, 'parties', result), {
                    name: details.name,
                    date: details.date,
                    emoji: details.emoji,
                    guestLimit: details.guestLimit,
                    location: details.location,
                    creatorId: user.uid,
                    creatorName: userProfile?.name || 'Unknown',
                    participants: [user.uid],
                    createdAt: serverTimestamp()
                });
            } catch (e) {
                console.error("Error creating party:", e);
                alert("Could not save party to database. Please check your connection or permissions.");
                return; // Stop if we can't save
            }
        }

        setIsCreateModalOpen(false);
        navigate(`/party/${result}`);
    };


    return (
        <div className="landing-container">
            <div className="landing-card">
                <div className="title-wrapper">
                    <h1 className="landing-title">🎉 Revlry</h1>
                    <span className="beta-badge">Beta</span>
                </div>
                <p className="landing-subtitle">Plan your next potluck or event with ease!</p>

                {/* Create Event Section - Now First */}
                <div className="landing-buttons">
                    <button className="create-btn" onClick={() => setIsCreateModalOpen(true)}>
                        <span className="btn-icon">✨</span> Create New Event
                    </button>
                </div>

                <div className="landing-divider">
                    <span>or</span>
                </div>

                {/* Join Party Section - Now Second with Better Label */}
                <div className="join-section">
                    <label className="join-label">Join an existing event with a code</label>
                    <form onSubmit={handleJoin} className="join-form">
                        <div className="input-group">
                            <input
                                type="text"
                                placeholder="XXX-XXX"
                                value={partyCode}
                                onChange={handleInput}
                                maxLength={7}
                                className="party-input"
                            />
                            <div className="char-count">
                                {partyCode.replace('-', '').length}/6
                            </div>
                        </div>
                        <button
                            type="submit"
                            className="join-btn"
                            disabled={partyCode.replace('-', '').length !== 6}
                        >
                            Join Event
                        </button>
                    </form>
                </div>

                {user && myParties.length > 0 && (
                    <div className="recent-section">
                        <h3>My Parties</h3>
                        <div className="my-parties-list">
                            {myParties.map((p) => (
                                <div key={p.code} className="party-card" onClick={() => handleJoin(null, p.code)}>
                                    <div className="party-info">
                                        <span className="party-name">{p.name || `Party ${p.code}`}</span>
                                        <span className="party-role">{p.creatorId === user.uid ? 'Host' : 'Guest'}</span>
                                    </div>
                                    <div className="party-meta">
                                        <span className="party-code">{formatCode(p.code)}</span>
                                        {p.date && <span className="party-date">{new Date(p.date).toLocaleDateString()}</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            <CreateEventModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onCreate={(details) => handleCreate(details)}
            />
        </div>
    );
}
